import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronLeft, Truck, CheckCircle2 } from 'lucide-react'
import { employeeApi } from '../../lib/employeeApi'
import { compressPhoto } from '../../lib/photo'
import { checkReception, parseTemp, QSE_DEFAULTS_VIEW, QseSettingsView, PRODUCT_TYPES, DECISIONS } from '../../lib/qseRules'

// Contrôle d'une livraison au téléphone : fournisseur, températures camion + colis,
// 3 contrôles visuels, décision, motif obligatoire si refus / réserve / non conforme.
export default function MobileReception() {
  const navigate = useNavigate()
  const [settings, setSettings] = useState<QseSettingsView>(QSE_DEFAULTS_VIEW)
  const [fournisseur, setFournisseur] = useState('')
  const [typeProduits, setTypeProduits] = useState('frais')
  const [tCamion, setTCamion] = useState('')
  const [tColis, setTColis] = useState('')
  const [dlcOk, setDlcOk] = useState(true)
  const [emballageOk, setEmballageOk] = useState(true)
  const [camionPropre, setCamionPropre] = useState(true)
  const [decision, setDecision] = useState('')
  const [motif, setMotif] = useState('')
  const [photo, setPhoto] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState<{ conforme: boolean; decision: string } | null>(null)

  useEffect(() => {
    employeeApi.get('/employee/me/qse-settings').then(r => setSettings({ ...QSE_DEFAULTS_VIEW, ...r.data })).catch(() => {})
  }, [])

  const sec = typeProduits === 'sec'
  const check = checkReception({ typeProduits, tCamion: parseTemp(tCamion), tColis: parseTemp(tColis), dlcOk, emballageOk, camionPropre }, settings)
  const aSaisir = !sec && parseTemp(tColis) == null
  const motifRequis = decision === 'refuse' || decision === 'reserve' || (decision === 'accepte' && !check.conforme && !aSaisir)
  const canSave = !!fournisseur.trim() && !aSaisir && !!decision && (!motifRequis || !!motif.trim()) && !saving

  async function pickPhoto(file?: File) {
    if (!file) return
    try { setPhoto(await compressPhoto(file)) } catch { setError('Photo illisible, réessayez') }
  }

  async function save() {
    setSaving(true); setError('')
    try {
      const r = await employeeApi.post('/employee/me/receptions', {
        fournisseur: fournisseur.trim(), typeProduits,
        tCamion: sec ? null : parseTemp(tCamion), tColis: sec ? null : parseTemp(tColis),
        dlcOk, emballageOk, camionPropre, decision, motif: motif.trim() || undefined,
        ...(photo ? { photo } : {}),
      })
      setDone({ conforme: r.data.conforme, decision })
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Enregistrement impossible, réessayez')
    } finally { setSaving(false) }
  }

  const Toggle = ({ label, value, onChange, ko }: { label: string; value: boolean; onChange: (v: boolean) => void; ko: string }) => (
    <div className="flex items-center justify-between gap-2">
      <span className="text-sm font-medium text-gray-800">{label}</span>
      <div className="flex rounded-xl overflow-hidden border border-gray-300 flex-shrink-0">
        <button onClick={() => onChange(true)} className={`px-4 py-2 text-sm font-semibold ${value ? 'bg-green-600 text-white' : 'bg-white text-gray-500'}`}>Oui</button>
        <button onClick={() => onChange(false)} className={`px-4 py-2 text-sm font-semibold ${!value ? 'bg-red-600 text-white' : 'bg-white text-gray-500'}`}>{ko}</button>
      </div>
    </div>
  )

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {error && (
        <div className="fixed bottom-4 left-4 right-4 z-50 bg-red-600 text-white text-sm rounded-xl px-4 py-3 shadow-lg" onClick={() => setError('')}>{error}</div>
      )}
      <div className="bg-white border-b px-4 flex items-center gap-3 sticky top-0 z-10" style={{ paddingTop: 'max(16px, env(safe-area-inset-top))', paddingBottom: '16px' }}>
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100"><ChevronLeft size={20} className="text-gray-600" /></button>
        <div className="flex-1">
          <h1 className="font-bold text-gray-900 flex items-center gap-2"><Truck size={18} className="text-orange-600" /> Contrôle livraison</h1>
          <p className="text-xs text-gray-500">À remplir au déchargement, avant de signer le BL</p>
        </div>
      </div>

      {done ? (
        <div className="p-6 text-center">
          <CheckCircle2 size={56} className={`mx-auto mb-3 ${done.conforme ? 'text-green-500' : 'text-orange-500'}`} />
          <p className="font-bold text-gray-900 text-lg">Livraison enregistrée</p>
          <p className="text-sm text-gray-600 mt-2">{done.conforme ? 'Conforme.' : 'Non conforme : c\'est noté dans le registre QSE.'}</p>
          <div className="mt-6 space-y-2">
            <button onClick={() => window.location.reload()} className="w-full border border-gray-300 bg-white font-bold py-3.5 rounded-xl">+ Autre livraison</button>
            <button onClick={() => navigate('/app/tasks')} className="w-full bg-blue-600 text-white font-bold py-3.5 rounded-xl">Retour à mes tâches</button>
          </div>
        </div>
      ) : (
        <div className="p-4 space-y-3 pb-32">
          <div className="bg-white rounded-2xl border p-4 space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-500">Fournisseur *</span>
              <input value={fournisseur} onChange={e => setFournisseur(e.target.value)} list="qse-fournisseurs" placeholder="Nom du fournisseur"
                className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-3 text-base" />
              <datalist id="qse-fournisseurs">{settings.fournisseurs.map(f => <option key={f} value={f} />)}</datalist>
            </label>
            <div className="flex flex-wrap gap-2">
              {settings.fournisseurs.map(f => (
                <button key={f} onClick={() => setFournisseur(f)}
                  className={`px-3 py-1.5 rounded-full text-sm border ${fournisseur === f ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}>{f}</button>
              ))}
            </div>
            <div>
              <span className="text-xs font-medium text-gray-500">Produits</span>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {PRODUCT_TYPES.map(t => (
                  <button key={t.value} onClick={() => setTypeProduits(t.value)}
                    className={`py-2.5 rounded-xl text-sm font-semibold border ${typeProduits === t.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}>{t.label}</button>
                ))}
              </div>
            </div>
          </div>

          {!sec && (
            <div className="bg-white rounded-2xl border p-4">
              <p className="text-sm font-bold text-gray-800 mb-2">Températures (max {typeProduits === 'surgele' ? settings.seuilSurgele : settings.seuilFrais} °C)</p>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="text-xs font-medium text-gray-500">Camion</span>
                  <input inputMode="decimal" value={tCamion} onChange={e => setTCamion(e.target.value)} placeholder="°C"
                    className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-3 text-lg font-semibold text-center" />
                </label>
                <label className="block">
                  <span className="text-xs font-medium text-gray-500">Colis (sonde) *</span>
                  <input inputMode="decimal" value={tColis} onChange={e => setTColis(e.target.value)} placeholder="°C"
                    className="mt-1 w-full border border-gray-300 rounded-xl px-3 py-3 text-lg font-semibold text-center" />
                </label>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border p-4 space-y-3">
            <Toggle label="Dates (DLC) correctes ?" value={dlcOk} onChange={setDlcOk} ko="Non" />
            <Toggle label="Emballages en bon état ?" value={emballageOk} onChange={setEmballageOk} ko="Non" />
            <Toggle label="Camion propre ?" value={camionPropre} onChange={setCamionPropre} ko="Non" />
          </div>

          {!aSaisir && (check.conforme
            ? <div className="bg-green-50 border-2 border-green-400 rounded-xl px-3 py-2 text-sm font-semibold text-green-800">✓ Livraison conforme</div>
            : <div className="bg-red-50 border-2 border-red-500 rounded-xl px-3 py-2 text-sm font-semibold text-red-800">✗ Non conforme : {check.raisons.join(', ')}</div>)}

          <div className="bg-white rounded-2xl border p-4 space-y-3">
            <span className="text-xs font-medium text-gray-500">Décision *</span>
            <div className="grid grid-cols-3 gap-2">
              {DECISIONS.map(d => (
                <button key={d.value} onClick={() => setDecision(d.value)}
                  className={`py-2.5 rounded-xl text-sm font-semibold border ${decision === d.value
                    ? (d.value === 'accepte' ? 'bg-green-600' : d.value === 'refuse' ? 'bg-red-600' : 'bg-orange-500') + ' text-white border-transparent'
                    : 'bg-white text-gray-700 border-gray-300'}`}>{d.label}</button>
              ))}
            </div>
            {motifRequis && (
              <textarea value={motif} onChange={e => setMotif(e.target.value)} rows={2}
                placeholder="Motif obligatoire (ce qui ne va pas, ce qui est écrit sur le BL)"
                className="w-full border-2 border-red-300 rounded-xl px-3 py-2 text-sm" />
            )}
            <label className={`flex items-center justify-center gap-2 py-3 rounded-xl border text-sm font-medium ${photo ? 'border-green-400 bg-green-50 text-green-700' : 'border-gray-300 bg-white text-gray-600'}`}>
              {photo ? <img src={photo} alt="" className="w-8 h-8 rounded object-cover" /> : <span>📷</span>}
              {photo ? 'Photo du BL ajoutée' : 'Photo du BL (facultative)'}
              <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => pickPhoto(e.target.files?.[0])} />
            </label>
          </div>
        </div>
      )}

      {!done && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4" style={{ paddingBottom: 'max(16px, env(safe-area-inset-bottom))' }}>
          {!canSave && !saving && (
            <p className="text-xs text-gray-500 mb-2 text-center">
              {!fournisseur.trim() ? 'Choisir le fournisseur' : aSaisir ? 'Saisir la température du colis' : !decision ? 'Choisir la décision' : 'Écrire le motif'}
            </p>
          )}
          <button onClick={save} disabled={!canSave} className="w-full bg-blue-600 text-white font-bold py-3.5 rounded-xl disabled:opacity-40 text-base">
            {saving ? 'Enregistrement...' : '✓ Enregistrer la livraison'}
          </button>
        </div>
      )}
    </div>
  )
}
